<template lang="pug">
  b-modal(
    v-model="show",
    size="lg",
    header-class="lmm-common-popup-header",
    dialog-class="mdm-config-dialog",
    :title="title",
    @hidden="reset")
    b-tabs(v-model="activeTab", content-class="mt-3")
      //- 탭 1: 설정
      b-tab(:title="$t('mdm_page.tab_settings')")
        b-form
          //- view 모드면 fieldset 통째 비활성 (읽기전용 상세 보기)
          fieldset(:disabled="mode === 'view'")
            b-form-group(:label="$t('mdm_page.config_name')")
              b-form-input(
                v-model="form.name",
                :maxlength="48",
                :placeholder="$t('mdm_page.config_name')",
                size="sm")
            //- v1 게이트: Android만 → OS 선택 숨김(생성 시 os_type은 emptyForm에서 "android" 고정). 되살리려면 주석 해제 + emptyForm os_type 원복
            //- b-form-group(:label="$t('mdm_page.os')")
            //-   b-form-select(
            //-     v-model="form.os_type",
            //-     :options="osOptions",
            //-     :disabled="mode === 'edit'",
            //-     size="sm",
            //-     @change="onOsChange")
            //-   b-form-text(v-if="mode === 'edit'") {{$t('mdm_page.os_immutable')}}
            b-form-group(:label="$t('mdm_page.description')")
              b-form-textarea(v-model="form.description", :maxlength="2048", :rows="2", :max-rows="5")
            b-form-group(:label="$t('mdm_page.location_track')")
              b-form-checkbox(v-model="form.location_track", switch) {{$t('mdm_page.location_track_use')}}
            b-form-group(:label="$t('mdm_page.info_update_interval')")
              b-input-group.mdm-interval(:append="$t('mdm_page.seconds')", size="sm")
                b-form-input(v-model.number="form.info_update_interval", type="number", min="1", size="sm")
            //- B4: Android 구성에서만 앱 업데이트 예약 노출 (OS별 동적 폼)
            template(v-if="isAndroid")
              hr
              b-form-group
                b-form-checkbox(v-model="form.update_schedule_enabled", switch) {{$t('mdm_page.update_schedule')}}
                b-form-text {{$t('mdm_page.update_schedule_help')}}
              div.d-flex.flex-wrap(v-if="form.update_schedule_enabled")
                b-form-group.mr-3(:label="$t('mdm_page.window_start')")
                  b-form-input(v-model="form.window_start_local", type="time", size="sm")
                b-form-group(:label="$t('mdm_page.window_end')")
                  b-form-input(v-model="form.window_end_local", type="time", size="sm")
      //- 탭 2: 애플리케이션 — 생성 모드는 pk 없어 비활성
      b-tab(:title="$t('mdm_page.tab_applications')", :disabled="mode === 'create'")
        p.text-muted.small.mb-0(v-if="mode === 'create'") {{$t('mdm_page.save_profile_first')}}
        mdm-profile-app-policy-tab(
          v-else,
          ref="appPolicy",
          :profile-pk="editPk",
          :mode="mode",
          @busy-change="onPolicyBusy",
          @saved="show = false")
    template(slot="modal-footer")
      //- view 모드
      template(v-if="mode === 'view'")
        b-btn(size="sm", variant="primary", @click="mode = 'edit'") {{$t('edit')}}
        b-btn(size="sm", variant="secondary", @click="show = false") {{$t('close')}}
      //- 애플리케이션 탭 저장
      template(v-else-if="activeTab === 1")
        b-btn(size="sm", variant="primary", :disabled="appPolicyBusy", @click="saveAppPolicy") {{$t('save')}}
        b-btn(size="sm", variant="secondary", @click="show = false") {{$t('cancel')}}
      //- 설정 탭 저장
      template(v-else)
        b-btn(size="sm", variant="primary", :disabled="saving", @click="submit")
          | {{mode === 'create' ? $t('create') : $t('save')}}
        b-btn(size="sm", variant="secondary", @click="show = false") {{$t('cancel')}}
</template>

<script>
// 기기정책(구성) 생성·수정·상세 모달 — create/edit/view 공용 (web3 b-modal 폼 패턴)
// 탭: [설정] 정책 폼 / [애플리케이션] 앱 설치 정책. 저장은 탭별 독립(백엔드 엔드포인트 분리).
// B3: 앱 업데이트 예약 시각은 time-of-day. 현지 시간대 입력 → UTC로 변환해 저장(영업시간 재설치 방지).
// B4: os_type === "android" 일 때만 예약 섹션을 노출하는 OS별 동적 폼.
import moment from "moment-timezone"
import { MdmProfileAPI } from "@/api/mdm"
import MdmProfileAppPolicyTab from "./MdmProfileAppPolicyTab.vue"

const emptyForm = () => ({
  name: "",
  os_type: "android", // v1 게이트: Android만 생성 가능. 원복: "windows" (+ 모달 OS 선택 주석 해제)
  description: "",
  location_track: false,
  info_update_interval: 300,
  update_schedule_enabled: false,
  window_start_local: "",
  window_end_local: "",
})

export default {
  name: "MdmProfileModal",
  components: { "mdm-profile-app-policy-tab": MdmProfileAppPolicyTab },
  data() {
    return {
      show: false,
      mode: "create",
      editPk: null,
      saving: false,
      activeTab: 0, // 0=설정 / 1=애플리케이션
      appPolicyBusy: false, // 앱 설치 정책 로딩·저장 중 [저장] 비활성
      form: emptyForm(),
      osOptions: [
        { value: "android", text: "Android" },
        // v1 게이트: Android만. OS 선택 되살릴 때 함께 주석 해제
        // { value: "windows", text: "Windows" },
        // { value: "osx", text: "macOS" },
      ],
    }
  },
  computed: {
    isAndroid() {
      return this.form.os_type === "android"
    },
    title() {
      if (this.mode === "create") return this.$t("mdm_page.add_config")
      if (this.mode === "view") return this.$t("mdm_page.view_config")
      return this.$t("mdm_page.edit_config")
    },
  },
  methods: {
    // 부모(MdmProfilePage)가 ref로 호출. mode="create" | "edit" | "view" (edit·view는 profile 필요)
    open(mode, profile) {
      this.mode = mode
      this.saving = false
      this.activeTab = 0 // 열 때는 항상 설정 탭부터
      this.appPolicyBusy = false
      if ((mode === "edit" || mode === "view") && profile) {
        this.editPk = profile.pk
        this.form = {
          name: profile.name || "",
          os_type: profile.os_type,
          description: profile.description || "",
          location_track: !!profile.location_track,
          info_update_interval: profile.info_update_interval || 300,
          update_schedule_enabled: !!profile.update_schedule_enabled,
          window_start_local: this.utcTimeToLocal(profile.app_update_start_time),
          window_end_local: this.utcTimeToLocal(profile.app_update_end_time),
        }
      } else {
        this.editPk = null
        this.form = emptyForm()
      }
      this.show = true
    },
    reset() {
      this.form = emptyForm()
      this.editPk = null
      this.saving = false
      this.activeTab = 0
      this.appPolicyBusy = false
    },
    onOsChange() {
      // 비-Android로 바꾸면 예약 필드 초기화 (백엔드가 비-Android+예약을 400으로 거부)
      if (!this.isAndroid) {
        this.form.update_schedule_enabled = false
        this.form.window_start_local = ""
        this.form.window_end_local = ""
      }
    },
    onPolicyBusy(busy) {
      this.appPolicyBusy = busy
    },
    // 앱 설치 정책 컴포넌트에 위임(ref)
    saveAppPolicy() {
      if (this.$refs.appPolicy) this.$refs.appPolicy.save()
    },
    localTimeToUtc(localTime) {
      if (!localTime) return null
      const tz = this.AppState.Info.timezone || moment.tz.guess()
      return moment.tz(`2000-01-01 ${localTime}`, "YYYY-MM-DD HH:mm", tz).utc().format("HH:mm:ss")
    },
    utcTimeToLocal(utcTime) {
      if (!utcTime) return ""
      const tz = this.AppState.Info.timezone || moment.tz.guess()
      return moment.utc(`2000-01-01 ${utcTime}`, "YYYY-MM-DD HH:mm:ss").tz(tz).format("HH:mm")
    },
    buildPayload(forCreate) {
      const payload = {
        name: (this.form.name || "").trim(),
        description: this.form.description ? this.form.description : null,
        location_track: this.form.location_track,
        info_update_interval: Number(this.form.info_update_interval),
      }
      // os_type은 생성 시에만 — 수정 요청 스키마엔 없음(불변)
      if (forCreate) payload.os_type = this.form.os_type
      if (this.isAndroid && this.form.update_schedule_enabled) {
        payload.update_schedule_enabled = true
        payload.app_update_start_time = this.localTimeToUtc(this.form.window_start_local)
        payload.app_update_end_time = this.localTimeToUtc(this.form.window_end_local)
      } else {
        payload.update_schedule_enabled = false
        payload.app_update_start_time = null
        payload.app_update_end_time = null
      }
      return payload
    },
    async submit() {
      if (this.saving) return
      if (!this.form.name || !this.form.name.trim()) {
        return this.$awn.warning(this.$t("mdm_page.name_required"))
      }
      if (!this.form.info_update_interval || Number(this.form.info_update_interval) <= 0) {
        return this.$awn.warning(this.$t("mdm_page.interval_required"))
      }
      if (
        this.isAndroid &&
        this.form.update_schedule_enabled &&
        (!this.form.window_start_local || !this.form.window_end_local)
      ) {
        return this.$awn.warning(this.$t("mdm_page.schedule_time_required"))
      }
      this.saving = true
      try {
        if (this.mode === "create") {
          await MdmProfileAPI.post("", this.buildPayload(true))
          this.$awn.success(this.$t("mdm_page.config_created"))
        } else {
          await MdmProfileAPI.put(`${this.editPk}`, this.buildPayload(false))
          this.$awn.success(this.$t("mdm_page.config_updated"))
        }
        this.show = false
        this.$emit("saved")
      } catch (error) {
        // 에러 토스트는 전역 인터셉터(api/config.js)가 처리 — department 패턴
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<!-- 모달은 렌더 시 <body>로 이동해 scoped CSS가 안 닿음 → non-scoped + 고유 클래스로 위치 조정 -->
<style lang="scss">
.mdm-config-dialog {
  // 기본(상단)보다 살짝 아래로. centered(정중앙)는 과해서 margin-top으로 미세 조정.
  margin-top: 8vh;

  // 정보 갱신 주기 입력 — 데스크탑 고정폭, 모바일은 좁은 화면 폭에 맞춰 풀폭
  .mdm-interval {
    width: 180px;
  }

  // 탭 — 목업 오렌지 배경 + 흰 글자 (이 모달 탭만)
  .nav-tabs .nav-link {
    // 콤팩트하게
    padding: 0.35rem 0.9rem;
    font-size: 0.875rem;
    color: #495057;
    transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;

    // 선택 탭
    &.active {
      background-color: #ec7211;
      color: #fff;
      border-color: #ec7211;
    }

    // 호버 (active 뒤 배치 = 선택 탭 호버에도 적용)
    &:hover {
      background-color: #b84e08;
      color: #fff;
      border-color: #b84e08;
    }

    // 비활성 탭은 주황 없이 흐리게
    &.disabled,
    &.disabled:hover {
      background-color: transparent;
      color: #adb5bd;
      border-color: transparent;
    }
  }

  // 모바일(xs): 풀스크린으로 꽉 채우지 않고 상단 여백만 줄여 화면 폭에 맞춤 (bootstrap 기본 dialog 여백 유지)
  @media (max-width: 575px) {
    margin-top: 1rem;

    .mdm-interval {
      width: 100%;
    }
  }
}
</style>
